import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { cn } from '../../lib/utils';
import { Search, Plus, User, Calendar, List, LayoutGrid, Phone, ArrowRight, ScanLine, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';
import * as patientApi from '../../lib/api/patientApi';

/* ───────────────────────────────────────────────────────────
 * Demo-only fixture. Rendered ONLY when `isDemo === true`. In
 * production / real mode the table is sourced from /api/patients
 * (see the searchPatients call below). This guard is enforced by
 * the new `real_mode_no_mock_dashboard.test.js` regression test.
 * ─────────────────────────────────────────────────────────── */
const DEMO_PATIENTS = [
  { id: 'P001', name: 'John Smith', dob: '1965-03-15', age: 60, gender: 'Male', mrn: 'MRN-001', insurance: 'Blue Cross', phone: '(555) 123-4567', lastVisit: '2026-02-03', nextAppt: '2026-03-17', status: 'active' },
  { id: 'P002', name: 'Maria Garcia', dob: '1978-07-22', age: 47, gender: 'Female', mrn: 'MRN-002', insurance: 'Aetna', phone: '(555) 234-5678', lastVisit: '2026-02-03', nextAppt: '2026-03-17', status: 'active' },
  { id: 'P003', name: 'Robert Johnson', dob: '1955-11-08', age: 70, gender: 'Male', mrn: 'MRN-003', insurance: 'Medicare', phone: '(555) 345-6789', lastVisit: '2026-02-02', nextAppt: null, status: 'active' },
  { id: 'P004', name: 'Emily Davis', dob: '1982-04-30', age: 43, gender: 'Female', mrn: 'MRN-004', insurance: 'UnitedHealth', phone: '(555) 456-7890', lastVisit: '2026-02-02', nextAppt: '2026-03-18', status: 'active' },
  { id: 'P005', name: 'Sarah Williams', dob: '1970-09-12', age: 55, gender: 'Female', mrn: 'MRN-005', insurance: 'Cigna', phone: '(555) 567-8901', lastVisit: '2026-02-01', nextAppt: null, status: 'inactive' },
  { id: 'P006', name: 'Michael Brown', dob: '1990-01-25', age: 35, gender: 'Male', mrn: 'MRN-006', insurance: 'Blue Cross', phone: '(555) 678-9012', lastVisit: '2026-01-28', nextAppt: '2026-03-20', status: 'active' },
];

/**
 * Normalize the backend patient document shape (snake_case + nested) to the
 * flat camelCase row the table expects. Defensive across the two shapes the
 * backend can return (legacy /patients vs vendor-aligned /api/patients).
 */
const ageFromDob = (dob) => {
  if (!dob) return '';
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
};
const normalizePatient = (p) => {
  const first = p.first_name || p.firstName || '';
  const last  = p.last_name  || p.lastName  || '';
  const name  = p.name || `${first} ${last}`.trim() || p.full_name || '—';
  return {
    id:        p.id || p._id || p.patient_id || p.mrn || name,
    name,
    dob:       p.dob || p.date_of_birth || '',
    age:       p.age ?? ageFromDob(p.dob || p.date_of_birth),
    gender:    p.gender || p.sex || '',
    mrn:       p.mrn || p.medical_record_number || p.id || '',
    insurance: p.insurance || p.primary_insurance || p.payer || '',
    phone:     p.phone || p.phone_number || p.mobile || '',
    lastVisit: p.last_visit || p.lastVisit || '',
    nextAppt:  p.next_appointment || p.nextAppt || null,
    status:    p.status || (p.active === false ? 'inactive' : 'active'),
  };
};

const EchoPatients = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [showAdd, setShowAdd] = useState(false);
  const [newPatient, setNewPatient] = useState({ firstName: '', lastName: '', dob: '', gender: '', phone: '', insurance: '' });

  /* ─── Real-mode data wiring (P0 fix) ─── */
  const [patients, setPatients] = useState(() => (isDemo ? DEMO_PATIENTS : []));
  const [loading, setLoading]   = useState(!isDemo);
  const [error, setError]       = useState(null);
  // Pagination — server-side. limit=50/page so initial load is fast;
  // user can page forward indefinitely or jump back. `has_more` from
  // the API drives the Next-button enable state; if the API doesn't
  // expose `total`, the page indicator shows `Page N` instead of N/M.
  const PAGE_SIZE = 50;
  const [page, setPage]     = useState(1);
  const [total, setTotal]   = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchPatients = useCallback(async (q = '', pg = 1) => {
    if (isDemo) {
      setPatients(DEMO_PATIENTS);
      setLoading(false);
      setTotal(DEMO_PATIENTS.length);
      setHasMore(false);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await patientApi.searchPatients({ query: q, limit: PAGE_SIZE, page: pg });
    if (res.ok) {
      const raw = res.data?.patients || res.data?.items || res.data || [];
      const arr = Array.isArray(raw) ? raw : [];
      setPatients(arr.map(normalizePatient));
      // Pagination envelope: { total, page, limit, has_more } when the
      // backend supports it; otherwise infer has_more from page-full.
      setTotal(typeof res.data?.total === 'number' ? res.data.total : null);
      setHasMore(
        typeof res.data?.has_more === 'boolean'
          ? res.data.has_more
          : arr.length >= PAGE_SIZE,
      );
      setPage(pg);
    } else {
      setError(res.error || { message: 'Failed to load patients' });
      setPatients([]);
      setTotal(null);
      setHasMore(false);
    }
    setLoading(false);
  }, [isDemo]);

  useEffect(() => { fetchPatients('', 1); }, [fetchPatients]);

  // Reset to page 1 whenever the search term changes (debounced via blur
  // / Enter would be nicer; the current input is already client-side filter
  // for the *loaded* page, so the user's typing experience stays snappy).
  const onSubmitSearch = useCallback(() => {
    fetchPatients(searchTerm, 1);
  }, [fetchPatients, searchTerm]);

  // Client-side filter on the already-loaded set (real backends with
  // millions of rows would defer to a server-side query, but the v1
  // contract returns full list-on-load).
  const filtered = patients.filter(p =>
    (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.mrn || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = async () => {
    if (!newPatient.firstName || !newPatient.lastName) { toast.error('Please enter patient name'); return; }
    // Real-mode: hit the registration endpoint when present; demo: optimistic toast.
    if (isDemo) {
      toast.success(`Patient ${newPatient.firstName} ${newPatient.lastName} registered`);
    } else {
      const res = await patientApi.createPatient
        ? await patientApi.createPatient({
            first_name: newPatient.firstName,
            last_name:  newPatient.lastName,
            dob:        newPatient.dob,
            gender:     newPatient.gender,
            phone:      newPatient.phone,
            insurance:  newPatient.insurance,
          })
        : { ok: false, error: { message: 'Patient creation endpoint unavailable' } };
      if (res.ok) {
        toast.success(`Patient ${newPatient.firstName} ${newPatient.lastName} registered`);
        fetchPatients('');
      } else {
        toast.error(res.error?.message || 'Could not register patient');
        return;
      }
    }
    setShowAdd(false);
    setNewPatient({ firstName: '', lastName: '', dob: '', gender: '', phone: '', insurance: '' });
  };

  return (
    <div className="space-y-6" data-testid="echo-patients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Patients</h1>
          <p className="text-slate-500 mt-1" data-testid="patients-count">
            {loading
              ? 'Loading patients…'
              : total !== null
                ? `${filtered.length} of ${total} patient${total === 1 ? '' : 's'}`
                : `${filtered.length} patient${filtered.length === 1 ? '' : 's'}${hasMore ? '+' : ''}`}
          </p>
        </div>
        <div className="flex gap-2">
          {!isDemo && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchPatients(searchTerm, page)}
              disabled={loading}
              className="gap-2"
              data-testid="patients-refresh-btn"
            >
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} /> Refresh
            </Button>
          )}
          <div className="flex rounded-lg border overflow-hidden">
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" className={cn("rounded-none gap-1.5", viewMode === 'list' && "bg-black text-white")} onClick={() => setViewMode('list')}><List className="h-4 w-4" /> List</Button>
            <Button variant={viewMode === 'grid' ? 'default' : 'ghost'} size="sm" className={cn("rounded-none gap-1.5", viewMode === 'grid' && "bg-black text-white")} onClick={() => setViewMode('grid')}><LayoutGrid className="h-4 w-4" /> Grid</Button>
          </div>
          <Button onClick={() => navigate('/echo/lens')} variant="outline" className="gap-2" data-testid="scan-patient-btn"><ScanLine className="h-4 w-4" /> Scan ID</Button>
          <Button onClick={() => setShowAdd(true)} className="bg-black hover:bg-gray-800 gap-2" data-testid="add-patient-btn"><Plus className="h-4 w-4" /> New Patient</Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search by name or MRN…"
          className="pl-10"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSubmitSearch(); }}
          onBlur={onSubmitSearch}
          data-testid="patient-search"
        />
      </div>

      {loading && patients.length === 0 ? (
        <Card className="border-slate-200" data-testid="patients-loading">
          <CardContent className="p-12 flex items-center justify-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading patients…</span>
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-red-200 bg-red-50" data-testid="patients-error">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mx-auto mb-1.5" />
            <p className="text-sm font-medium text-red-700">Could not load patients</p>
            <p className="text-[11px] text-red-600 mt-0.5">{error.message || 'Server error — please retry.'}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3 border-red-300 text-red-700"
              onClick={() => fetchPatients(searchTerm)}
              data-testid="patients-retry-btn"
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-slate-200" data-testid="patients-empty">
          <CardContent className="p-12 text-center">
            <User className="h-6 w-6 text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-600">
              {searchTerm ? 'No patients match your search' : 'No patients yet'}
            </p>
            <p className="text-[11px] text-slate-400 mt-1">
              {searchTerm
                ? 'Try a different name or MRN.'
                : 'Register a patient or scan an ID card to get started.'}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        <Card className="border-slate-200">
          <Table>
            <TableHeader><TableRow><TableHead>Patient</TableHead><TableHead>MRN</TableHead><TableHead>DOB / Age</TableHead><TableHead>Insurance</TableHead><TableHead>Phone</TableHead><TableHead>Next Appt</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {filtered.map(p => (
                <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/echo/patients/${p.id}`)} data-testid={`patient-row-${p.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center"><User className="h-4 w-4 text-slate-500" /></div>
                      <div><p className="font-medium text-sm">{p.name}</p><p className="text-[11px] text-muted-foreground">{p.gender}</p></div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.mrn}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.dob} ({p.age}y)</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{p.insurance}</Badge></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.phone}</TableCell>
                  <TableCell className="text-sm">{p.nextAppt ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{p.nextAppt}</Badge> : <span className="text-muted-foreground text-xs">None</span>}</TableCell>
                  <TableCell><ArrowRight className="h-4 w-4 text-slate-400" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => (
            <Card key={p.id} className="border-slate-200 hover:border-slate-300 cursor-pointer transition-all" onClick={() => navigate(`/echo/patients/${p.id}`)} data-testid={`patient-card-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center"><User className="h-5 w-5 text-slate-500" /></div>
                  <div><p className="font-medium text-sm">{p.name}</p><p className="text-xs text-muted-foreground">{p.mrn} &middot; {p.gender}, {p.age}y</p></div>
                </div>
                <div className="space-y-1.5 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2"><Phone className="h-3 w-3" />{p.phone}</div>
                  <div className="flex items-center gap-2"><Calendar className="h-3 w-3" />Last: {p.lastVisit}</div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Badge variant="outline" className="text-[10px]">{p.insurance}</Badge>
                  {p.nextAppt && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Appt {p.nextAppt}</Badge>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Pagination controls (real mode only) ── */}
      {!isDemo && !loading && !error && (patients.length > 0 || page > 1) && (
        <div className="flex items-center justify-between mt-4" data-testid="patients-pager">
          <span className="text-[11px] text-slate-500" data-testid="patients-pager-status">
            {total !== null
              ? `Page ${page} · showing ${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + patients.length} of ${total}`
              : `Page ${page}${hasMore ? ' · more available' : ''}`}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchPatients(searchTerm, page - 1)}
              disabled={page <= 1 || loading}
              data-testid="patients-prev-btn"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchPatients(searchTerm, page + 1)}
              disabled={!hasMore || loading}
              data-testid="patients-next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md" data-testid="add-patient-dialog">
          <DialogHeader><DialogTitle>Register New Patient</DialogTitle><DialogDescription>Enter patient demographics. Use Echo Lens to scan an ID for auto-fill.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First Name</Label><Input value={newPatient.firstName} onChange={e => setNewPatient({ ...newPatient, firstName: e.target.value })} /></div>
            <div><Label>Last Name</Label><Input value={newPatient.lastName} onChange={e => setNewPatient({ ...newPatient, lastName: e.target.value })} /></div>
            <div><Label>Date of Birth</Label><Input type="date" value={newPatient.dob} onChange={e => setNewPatient({ ...newPatient, dob: e.target.value })} /></div>
            <div><Label>Gender</Label><Select value={newPatient.gender} onValueChange={v => setNewPatient({ ...newPatient, gender: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent></Select></div>
            <div><Label>Phone</Label><Input value={newPatient.phone} onChange={e => setNewPatient({ ...newPatient, phone: e.target.value })} /></div>
            <div><Label>Insurance</Label><Input value={newPatient.insurance} onChange={e => setNewPatient({ ...newPatient, insurance: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { navigate('/echo/lens'); setShowAdd(false); }} className="gap-2"><ScanLine className="h-4 w-4" /> Scan ID Instead</Button>
            <Button onClick={handleAdd} className="bg-black hover:bg-gray-800" data-testid="register-patient-btn">Register Patient</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EchoPatients;
